import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { apiFetch } from './WorkSheetContext';
import { getUserInfo } from './storage';

const WarehouseDataContext = createContext(null);

const STALE_MS = 2 * 60 * 1000;

const normalizeId = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return String(value._id || value.id || '');
    return String(value);
};

const buildPackingItemKey = (food, size) => `${normalizeId(food)}__${normalizeId(size)}`;

const getEntityTimestamp = (entity) => {
    const createdAtTs = new Date(entity?.createdAt || '').getTime();
    if (!Number.isNaN(createdAtTs) && createdAtTs > 0) return createdAtTs;

    const id = String(entity?._id || entity?.id || '');
    const match = id.match(/^[a-fA-F0-9]{24}$/);
    if (match) {
        const seconds = parseInt(id.slice(0, 8), 16);
        if (!Number.isNaN(seconds)) return seconds * 1000;
    }

    return 0;
};

const getDefaultDateRange = () => {
    const now = new Date();
    const startDate = new Date(now);
    const endDate = new Date(now);

    startDate.setDate(startDate.getDate() - 50);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    };
};

export const WarehouseDataProvider = ({ children }) => {
    const [restaurantId, setRestaurantId] = useState('');
    const [orders, setOrders] = useState([]);
    const [pickings, setPickings] = useState([]);
    const [inbounds, setInbounds] = useState([]);
    const [foods, setFoods] = useState([]);
    const [packagingFoods, setPackagingFoods] = useState([]);
    const [partners, setPartners] = useState([]);

    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingPickings, setLoadingPickings] = useState(false);
    const [loadingInbounds, setLoadingInbounds] = useState(false);
    const [loadingFoods, setLoadingFoods] = useState(false);
    const [loadingPackagingFoods, setLoadingPackagingFoods] = useState(false);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [loadingStocks, setLoadingStocks] = useState(false);
    const [stocks, setStocks] = useState({});
    const [errorOrders, setErrorOrders] = useState(null);
    const [errorPickings, setErrorPickings] = useState(null);
    const [errorInbounds, setErrorInbounds] = useState(null);
    const [errorFoods, setErrorFoods] = useState(null);
    const [errorPackagingFoods, setErrorPackagingFoods] = useState(null);
    const [errorPartners, setErrorPartners] = useState(null);
    const [errorStocks, setErrorStocks] = useState(null);

    const [ordersFetchedAt, setOrdersFetchedAt] = useState(0);
    const [pickingsFetchedAt, setPickingsFetchedAt] = useState(0);
    const [inboundsFetchedAt, setInboundsFetchedAt] = useState(0);
    const [foodsFetchedAt, setFoodsFetchedAt] = useState(0);
    const [packagingFoodsFetchedAt, setPackagingFoodsFetchedAt] = useState(0);
    const [partnersFetchedAt, setPartnersFetchedAt] = useState(0);

    const mountedRef = useRef(true);
    const ordersRef = useRef([]);
    const pickingsRef = useRef([]);
    const inboundsRef = useRef([]);
    const foodsRef = useRef([]);
    const packagingFoodsRef = useRef([]);
    const partnersRef = useRef([]);
    const stocksRef = useRef({});
    const ordersFetchedAtRef = useRef(0);
    const pickingsFetchedAtRef = useRef(0);
    const inboundsFetchedAtRef = useRef(0);
    const foodsFetchedAtRef = useRef(0);
    const packagingFoodsFetchedAtRef = useRef(0);
    const partnersFetchedAtRef = useRef(0);
    const stocksFetchedAtRef = useRef(0);
    const ordersInFlightRef = useRef(null);
    const pickingsInFlightRef = useRef(null);
    const inboundsInFlightRef = useRef(null);
    const foodsInFlightRef = useRef(null);
    const packagingFoodsInFlightRef = useRef(null);
    const partnersInFlightRef = useRef(null);
    const stocksInFlightRef = useRef(null);

	// const restaurantId = '642980214ee40922cfedebe3';
	const testRestaurantId = restaurantId;

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        ordersRef.current = orders;
    }, [orders]);

    useEffect(() => {
        pickingsRef.current = pickings;
    }, [pickings]);

    useEffect(() => {
        inboundsRef.current = inbounds;
    }, [inbounds]);

    useEffect(() => {
        foodsRef.current = foods;
    }, [foods]);

    useEffect(() => {
        packagingFoodsRef.current = packagingFoods;
    }, [packagingFoods]);

    useEffect(() => {
        partnersRef.current = partners;
    }, [partners]);

    useEffect(() => {
        stocksRef.current = stocks;
    }, [stocks]);

    useEffect(() => {
        ordersFetchedAtRef.current = ordersFetchedAt;
    }, [ordersFetchedAt]);

    useEffect(() => {
        pickingsFetchedAtRef.current = pickingsFetchedAt;
    }, [pickingsFetchedAt]);

    useEffect(() => {
        inboundsFetchedAtRef.current = inboundsFetchedAt;
    }, [inboundsFetchedAt]);

    useEffect(() => {
        foodsFetchedAtRef.current = foodsFetchedAt;
    }, [foodsFetchedAt]);

    useEffect(() => {
        packagingFoodsFetchedAtRef.current = packagingFoodsFetchedAt;
    }, [packagingFoodsFetchedAt]);

    useEffect(() => {
        partnersFetchedAtRef.current = partnersFetchedAt;
    }, [partnersFetchedAt]);

    useEffect(() => {
        getUserInfo().then((info) => {
            if (!mountedRef.current) return;

            if (info?.restaurants?.[0]?._id) {
                setRestaurantId(String(info.restaurants[0]._id));
            }
        });
    }, []);

    const shouldFetch = useCallback((fetchedAt, force = false) => {
        if (force) return true;
        if (!fetchedAt) return true;
        return Date.now() - fetchedAt > STALE_MS;
    }, []);

    const fetchOrders = useCallback(async (options = {}) => {
        if (!restaurantId) return [];

        const { force = false, dateRange } = options;
        if (!shouldFetch(ordersFetchedAtRef.current, force) && ordersRef.current.length > 0) {
            return ordersRef.current;
        }

        if (ordersInFlightRef.current) {
            return ordersInFlightRef.current;
        }

        const range = dateRange || getDefaultDateRange();

        const requestPromise = (async () => {
            setLoadingOrders(true);
            setErrorOrders(null);
            try {

                const query = new URLSearchParams({
                    startDate: range.startDate,
                    endDate: range.endDate,
                }).toString();

                const data = await apiFetch(`/restaurant/${restaurantId}/order?${query}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });

                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.orders)
                        ? data.orders
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

                if (mountedRef.current) {
                    setOrders(list);
                    setOrdersFetchedAt(Date.now());
                }

                return list;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorOrders(error);
                }
                return [];
            } finally {
                if (mountedRef.current) {
                    setLoadingOrders(false);
                }
                ordersInFlightRef.current = null;
            }
        })();

        ordersInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchPickings = useCallback(async (options = {}) => {
        if (!restaurantId) return [];

        const { force = false } = options;
        if (!shouldFetch(pickingsFetchedAtRef.current, force) && pickingsRef.current.length > 0) {
            return pickingsRef.current;
        }

        if (pickingsInFlightRef.current) {
            return pickingsInFlightRef.current;
        }

        const requestPromise = (async () => {
            setLoadingPickings(true);
            setErrorPickings(null);
            try {
                const data = await apiFetch(`/stock-reservation/restaurant/${restaurantId}`);
                const list = Array.isArray(data) ? data : [];

                if (mountedRef.current) {
                    setPickings(list);
                    setPickingsFetchedAt(Date.now());
                }

                return list;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorPickings(error);
                }
                return [];
            } finally {
                if (mountedRef.current) {
                    setLoadingPickings(false);
                }
                pickingsInFlightRef.current = null;
            }
        })();

        pickingsInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchInbounds = useCallback(async (options = {}) => {
        if (!restaurantId) return [];

        const { force = false } = options;
        if (!shouldFetch(inboundsFetchedAtRef.current, force) && inboundsRef.current.length > 0) {
            return inboundsRef.current;
        }

        if (inboundsInFlightRef.current) {
            return inboundsInFlightRef.current;
        }

        const requestPromise = (async () => {
            setLoadingInbounds(true);
            setErrorInbounds(null);
            try {
                const data = await apiFetch(`/stock-inbound/restaurant/${restaurantId}`);
                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.inbounds)
                        ? data.inbounds
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

                if (mountedRef.current) {
                    setInbounds(list);
                    setInboundsFetchedAt(Date.now());
                }

                return list;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorInbounds(error);
                }
                return [];
            } finally {
                if (mountedRef.current) {
                    setLoadingInbounds(false);
                }
                inboundsInFlightRef.current = null;
            }
        })();

        inboundsInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchFoods = useCallback(async (options = {}) => {
        if (!restaurantId) return [];

        const { force = false } = options;
        if (!shouldFetch(foodsFetchedAtRef.current, force) && foodsRef.current.length > 0) {
            return foodsRef.current;
        }

        if (foodsInFlightRef.current) {
            return foodsInFlightRef.current;
        }

        const requestPromise = (async () => {
            setLoadingFoods(true);
            setErrorFoods(null);
            try {
                const data = await apiFetch(`/restaurant/${restaurantId}/foods?page=1&limit=500&showDisabled=false`);
                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.foods)
                        ? data.foods
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

                if (mountedRef.current) {
                    setFoods(list);
                    setFoodsFetchedAt(Date.now());
                }

                return list;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorFoods(error);
                }
                return [];
            } finally {
                if (mountedRef.current) {
                    setLoadingFoods(false);
                }
                foodsInFlightRef.current = null;
            }
        })();

        foodsInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchPackagingFoods = useCallback(async (options = {}) => {
        if (!restaurantId) return [];

        const { force = false } = options;
        if (!shouldFetch(packagingFoodsFetchedAtRef.current, force) && packagingFoodsRef.current.length > 0) {
            return packagingFoodsRef.current;
        }

        if (packagingFoodsInFlightRef.current) {
            return packagingFoodsInFlightRef.current;
        }

        const requestPromise = (async () => {
            setLoadingPackagingFoods(true);
            setErrorPackagingFoods(null);
            try {
                const data = await apiFetch(`/restaurant/${restaurantId}/foods?foodType=packaging&page=1&limit=150&showDisabled=false`);
                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.foods)
                        ? data.foods
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

                if (mountedRef.current) {
                    setPackagingFoods(list);
                    setPackagingFoodsFetchedAt(Date.now());
                }

                return list;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorPackagingFoods(error);
                }
                return [];
            } finally {
                if (mountedRef.current) {
                    setLoadingPackagingFoods(false);
                }
                packagingFoodsInFlightRef.current = null;
            }
        })();

        packagingFoodsInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchStocks = useCallback(async (options = {}) => {
        if (!restaurantId) return {};

        const { force = false } = options;
        if (!shouldFetch(stocksFetchedAtRef.current, force) && Object.keys(stocksRef.current).length > 0) {
            return stocksRef.current;
        }

        if (stocksInFlightRef.current) {
            return stocksInFlightRef.current;
        }

        const requestPromise = (async () => {
            setLoadingStocks(true);
            setErrorStocks(null);
            try {
                // Fetch warehouses for fallback
                let fallbackWarehouseId = null;
                try {
                    const warehouseList = await apiFetch(`/warehouse/warehouses?restaurantId=${restaurantId}`);
                    if (Array.isArray(warehouseList) && warehouseList.length > 0) {
                        fallbackWarehouseId = warehouseList[0]?._id || warehouseList[0]?.id;
                    } else {
                        console.warn('FetchStocks: No warehouses found for restaurant');
                    }
                } catch (err) {
                    console.error('FetchStocks: Error fetching warehouses:', err);
                }

                // Gather unique warehouse IDs from inbounds items' food sizes positions
                const warehouseIds = new Set();
                if (fallbackWarehouseId) {
                    warehouseIds.add(fallbackWarehouseId);
                }
                inboundsRef.current.forEach((inbound) => {
                    const items = Array.isArray(inbound?.items) ? inbound.items : [];

                    items.forEach((row) => {
                        const sizes = Array.isArray(row?.food?.sizes) ? row.food.sizes : [];

                        sizes.forEach((size) => {
                            let warehouseId = size?.position?.warehouse;

                            // Use fallback warehouse if not specified in position
                            if (!warehouseId && fallbackWarehouseId) {
                                warehouseId = fallbackWarehouseId;
                            }

                            if (warehouseId) {
                                warehouseIds.add(warehouseId);
                            }
                        });
                    });
                });

                if (warehouseIds.size === 0) {
                    if (mountedRef.current) {
                        setStocks({});
                    }
                    return {};
                }

                // Fetch stocks for each warehouse
                const stockPromises = Array.from(warehouseIds).map((warehouseId) => {
                    return apiFetch(`/warehouse/${warehouseId}/stock`).catch((err) => {
                        console.error(`FetchStocks: Error fetching warehouse ${warehouseId}:`, err);
                        return [];
                    });
                });

                const results = await Promise.all(stockPromises);

                // Build map: {foodId}_{sizeId} -> stockQty
                const stockMap = {};
                results.forEach((stockList) => {
                    if (Array.isArray(stockList)) {
                        stockList.forEach((stock) => {
                            const itemId = stock?.item?._id;
                            const sizeId = stock?.size;
                            if (itemId && sizeId) {
                                const key = `${itemId}_${sizeId}`;
                                stockMap[key] = (stockMap[key] || 0) + Number(stock?.stockQty || 0);
                            }
                        });
                    }
                });

                if (mountedRef.current) {
                    setStocks(stockMap);
                    stocksFetchedAtRef.current = Date.now();
                }

                return stockMap;
            } catch (error) {
                console.error('FetchStocks: Error:', error);
                if (mountedRef.current) {
                    setErrorStocks(error);
                }
                return {};
            } finally {
                if (mountedRef.current) {
                    setLoadingStocks(false);
                }
                stocksInFlightRef.current = null;
            }
        })();

        stocksInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchPartners = useCallback(async (options = {}) => {
        if (!restaurantId) return [];

        const { force = false } = options;
        if (!shouldFetch(partnersFetchedAtRef.current, force) && partnersRef.current.length > 0) {
            return partnersRef.current;
        }

        if (partnersInFlightRef.current) {
            return partnersInFlightRef.current;
        }

        const requestPromise = (async () => {
            setLoadingPartners(true);
            setErrorPartners(null);
            try {
                const params = new URLSearchParams({
                    restaurantId: String(restaurantId),
                    enabled: 'true',
                });
                const data = await apiFetch(`/partner?${params.toString()}`);
                const rawList = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.partners)
                        ? data.partners
                        : Array.isArray(data?.data)
                            ? data.data
                            : [];

                // Fallback guard in case backend ignores enabled filter.
                const list = rawList.filter((partner) => {
                    if (!partner || typeof partner !== 'object') return false;
                    if (partner.enabled === false) return false;
                    if (String(partner.enabled).toLowerCase() === 'false') return false;
                    if (partner.deleted === true) return false;
                    if (partner.isDeleted === true) return false;
                    if (partner.deletedAt) return false;
                    if (String(partner.status || '').toLowerCase() === 'deleted') return false;
                    return true;
                });

                if (mountedRef.current) {
                    setPartners(list);
                    setPartnersFetchedAt(Date.now());
                }

                return list;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorPartners(error);
                }
                return [];
            } finally {
                if (mountedRef.current) {
                    setLoadingPartners(false);
                }
                partnersInFlightRef.current = null;
            }
        })();

        partnersInFlightRef.current = requestPromise;
        return requestPromise;
    }, [restaurantId, shouldFetch]);

    const fetchLatestPackedAmountsForOrder = useCallback(async (orderId) => {
        const resolvedOrderId = normalizeId(orderId);
        if (!resolvedOrderId) return {};

        try {
            const response = await apiFetch(`/stock-packing/order/${resolvedOrderId}`);
            const rows = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response)
                    ? response
                    : [];

            const latestPacked = rows
                .filter((row) => String(row?.status || '').toLowerCase() === 'packed')
                .sort((a, b) => getEntityTimestamp(b) - getEntityTimestamp(a))[0];

            const items = Array.isArray(latestPacked?.items) ? latestPacked.items : [];
            const itemMap = {};
            items.forEach((item) => {
                const key = buildPackingItemKey(item?.food, item?.size);
                if (!key || key === '__') return;

                const current = Number(itemMap[key] ?? 0);
                itemMap[key] = current + Number(item?.amount ?? item?.quantity ?? 0);
            });

            return itemMap;
        } catch {
            return {};
        }
    }, []);

    const fetchLatestPackedAmountsByOrderIds = useCallback(async (orderIds = []) => {
        const result = {};
        const normalizedOrderIds = Array.isArray(orderIds)
            ? orderIds.map((value) => normalizeId(value)).filter(Boolean)
            : [];

        await Promise.all(normalizedOrderIds.map(async (orderId) => {
            result[orderId] = await fetchLatestPackedAmountsForOrder(orderId);
        }));

        return result;
    }, [fetchLatestPackedAmountsForOrder]);

    const ensureWarehouseData = useCallback(async (options = {}) => {
        const {
            includeOrders = true,
            includePickings = true,
            includeInbounds = false,
            includeFoods = false,
            includePackagingFoods = false,
            includePartners = true,
            includeStocks = false,
            force = false,
            dateRange,
        } = options;

        const jobs = [];

        if (includeOrders) {
            jobs.push(fetchOrders({ force, dateRange }));
        }

        if (includePickings) {
            jobs.push(fetchPickings({ force }));
        }

        if (includeInbounds) {
            jobs.push(fetchInbounds({ force }));
        }

        if (includeFoods) {
            jobs.push(fetchFoods({ force }));
        }

        if (includePackagingFoods) {
            jobs.push(fetchPackagingFoods({ force }));
        }

        if (includePartners) {
            jobs.push(fetchPartners({ force }));
        }

        // Run main jobs in parallel
        if (jobs.length > 0) {
            await Promise.all(jobs);
        }

        // Stocks must be fetched AFTER inbounds, so run it separately if needed
        if (includeStocks) {
            await fetchStocks({ force });
        }
    }, [fetchOrders, fetchPickings, fetchInbounds, fetchFoods, fetchPackagingFoods, fetchPartners, fetchStocks]);

    const refreshWarehouseData = useCallback(async (options = {}) => {
        await ensureWarehouseData({
            includeOrders: options.includeOrders !== false,
            includePickings: options.includePickings !== false,
            includeInbounds: options.includeInbounds === true,
            includeFoods: options.includeFoods === true,
            includePackagingFoods: options.includePackagingFoods === true,
            includePartners: options.includePartners !== false,
            includeStocks: options.includeStocks === true,
            dateRange: options.dateRange,
            force: true,
        });
    }, [ensureWarehouseData]);

    useEffect(() => {
        if (!restaurantId) return;

        void ensureWarehouseData({ includeOrders: true, includePickings: true, includePackagingFoods: false });
    }, [restaurantId, ensureWarehouseData]);

    const pickingsByOrderId = useMemo(() => {
        const map = new Map();

        pickings.forEach((picking) => {
            const orderId = normalizeId(picking?.order?._id || picking?.order);
            if (!orderId) return;

            const group = map.get(orderId) || [];
            group.push(picking);
            map.set(orderId, group);
        });

        return map;
    }, [pickings]);

    const ordersById = useMemo(() => {
        const map = new Map();

        orders.forEach((order) => {
            const orderId = normalizeId(order?._id);
            if (!orderId) return;
            map.set(orderId, order);
        });

        return map;
    }, [orders]);

    const mergedOrders = useMemo(() => {
        return orders.map((order) => {
            const orderId = normalizeId(order?._id);
            const relatedPickings = pickingsByOrderId.get(orderId) || [];

            return {
                ...order,
                relatedPickings,
                hasPicking: relatedPickings.length > 0,
            };
        });
    }, [orders, pickingsByOrderId]);

    const mergedPickings = useMemo(() => {
        return pickings.map((picking) => {
            const orderId = normalizeId(picking?.order?._id || picking?.order);
            const relatedOrder = ordersById.get(orderId) || null;

            return {
                ...picking,
                relatedOrder,
                hasRelatedOrder: Boolean(relatedOrder),
            };
        });
    }, [ordersById, pickings]);

    const contextValue = useMemo(() => ({
        restaurantId,
        orders,
        pickings,
        inbounds,
        foods,
        packagingFoods,
        partners,
        stocks,
        mergedOrders,
        mergedPickings,
        loadingOrders,
        loadingPickings,
        loadingInbounds,
        loadingFoods,
        loadingPackagingFoods,
        loadingPartners,
        loadingStocks,
        errorOrders,
        errorPickings,
        errorInbounds,
        errorFoods,
        errorPackagingFoods,
        errorPartners,
        errorStocks,
        ensureWarehouseData,
        refreshWarehouseData,
        setPickings,
        fetchLatestPackedAmountsForOrder,
        fetchLatestPackedAmountsByOrderIds,
    }), [
        restaurantId,
        orders,
        pickings,
        inbounds,
        foods,
        packagingFoods,
        partners,
        stocks,
        mergedOrders,
        mergedPickings,
        loadingOrders,
        loadingPickings,
        loadingInbounds,
        loadingFoods,
        loadingPackagingFoods,
        loadingPartners,
        loadingStocks,
        errorOrders,
        errorPickings,
        errorInbounds,
        errorFoods,
        errorPackagingFoods,
        errorPartners,
        errorStocks,
        ensureWarehouseData,
        refreshWarehouseData,
        fetchLatestPackedAmountsForOrder,
        fetchLatestPackedAmountsByOrderIds,
    ]);

    return (
        <WarehouseDataContext.Provider value={contextValue}>
            {children}
        </WarehouseDataContext.Provider>
    );
};

export const useWarehouseData = () => {
    const context = useContext(WarehouseDataContext);
    if (!context) {
        throw new Error('useWarehouseData must be used inside WarehouseDataProvider');
    }
    return context;
};
